import { differenceInDays, differenceInMonths, parseISO, startOfQuarter, isAfter } from 'date-fns';

export const STATUSES = [
  { key: 'ideas', label: 'Ideas', hint: 'Parked until we decide', dot: 'var(--slate)' },
  { key: 'planned', label: 'Planned', hint: 'Agreed, not started', dot: 'var(--honey)' },
  { key: 'in_progress', label: 'In progress', hint: 'Someone is on it', dot: 'var(--apricot)' },
  { key: 'done', label: 'Done', hint: 'Shipped', dot: 'var(--sage)' },
];

export const GOAL_STATUSES = {
  on_track: { label: 'On track', bg: 'var(--sage-soft)', fg: 'var(--sage-deep)', bar: 'var(--sage)' },
  at_risk: { label: 'At risk', bg: 'var(--honey-soft)', fg: 'var(--honey-deep)', bar: 'var(--honey)' },
  off_track: { label: 'Off track', bg: 'var(--rose-soft)', fg: 'var(--rose-deep)', bar: 'var(--rose)' },
  achieved: { label: 'Achieved', bg: 'var(--sage-deep)', fg: '#fff', bar: 'var(--sage-deep)' },
  dropped: { label: 'Dropped', bg: 'var(--paper-3)', fg: 'var(--ink-3)', bar: 'var(--ink-3)' },
};

export const FLAGS = {
  urgent: { label: 'Urgent', fg: 'var(--rose-deep)', bg: 'var(--rose-soft)' },
  blocked: { label: 'Blocked', fg: 'var(--rose-deep)', bg: 'var(--rose-soft)' },
  quick_win: { label: 'Quick win', fg: 'var(--sage-deep)', bg: 'var(--sage-soft)' },
  waiting: { label: 'Waiting on', fg: 'var(--honey-deep)', bg: 'var(--honey-soft)' },
  idea: { label: 'Idea', fg: 'var(--slate-deep)', bg: 'var(--slate-soft)' },
};

export const TAG_COLOURS = {
  sage: { bg: 'var(--sage-soft)', fg: 'var(--sage-deep)' },
  apricot: { bg: 'var(--apricot-soft)', fg: 'var(--apricot-deep)' },
  honey: { bg: 'var(--honey-soft)', fg: 'var(--honey-deep)' },
  slate: { bg: 'var(--slate-soft)', fg: 'var(--slate-deep)' },
  lilac: { bg: '#E8E4F3', fg: '#564C85' },
  teal: { bg: '#DFEEEB', fg: '#2F6B64' },
  paper: { bg: 'var(--paper-3)', fg: 'var(--ink-2)' },
  rose: { bg: 'var(--rose-soft)', fg: 'var(--rose-deep)' },
};

export const OWNER_COLOURS = ['sage', 'apricot', 'honey', 'slate', 'lilac', 'teal'];

export const HORIZONS = [
  { key: 'h3', label: 'Next 3 months', color: 'var(--apricot)', soft: 'var(--apricot-soft)', deep: 'var(--apricot-deep)' },
  { key: 'h6', label: '6 months', color: 'var(--honey)', soft: 'var(--honey-soft)', deep: 'var(--honey-deep)' },
  { key: 'h12', label: '12 months', color: 'var(--sage)', soft: 'var(--sage-soft)', deep: 'var(--sage-deep)' },
  { key: 'h24', label: '24 months +', color: 'var(--slate)', soft: 'var(--slate-soft)', deep: 'var(--slate-deep)' },
];

export const TIERS = {
  year: { label: 'Big goal', plural: 'The big goal', color: 'var(--apricot)', soft: 'var(--apricot-soft)', deep: 'var(--apricot-deep)' },
  quarter: { label: 'Quarterly goal', plural: 'Quarterly goals', color: 'var(--honey)', soft: 'var(--honey-soft)', deep: 'var(--honey-deep)' },
  mini: { label: 'Mini goal', plural: 'Mini goals', color: 'var(--sage)', soft: 'var(--sage-soft)', deep: 'var(--sage-deep)' },
  future: { label: 'Future goal', plural: 'Future goals', color: 'var(--slate)', soft: 'var(--slate-soft)', deep: 'var(--slate-deep)' },
};
export const QUARTERS = [{ q: 1, label: 'Q1', months: 'Jan – Mar' }, { q: 2, label: 'Q2', months: 'Apr – Jun' }, { q: 3, label: 'Q3', months: 'Jul – Sep' }, { q: 4, label: 'Q4', months: 'Oct – Dec' }];
export const MINI_SLOTS = 6;
export const tierOf = (g) => g.tier || 'future';
export const currentQuarter = (todayStr) => Math.floor((todayStr ? parseISO(todayStr) : new Date()).getMonth() / 3) + 1;
export const slotLabel = (g) => {
  const t = tierOf(g);
  if (t === 'year') return `Big goal · ${g.plan_year}`;
  if (t === 'quarter') return `Q${g.quarter} ${g.plan_year}`;
  if (t === 'mini') return `Mini · ${g.plan_year}`;
  return 'Future';
};
// The plan for one year: fixed slots (1 big, 4 quarters, 6 minis) plus the parked future goals. Dropped goals free their slot.
export const planFor = (goals, year) => {
  const live = goals.filter((g) => g.status !== 'dropped');
  const inYear = live.filter((g) => g.plan_year === year && tierOf(g) !== 'future');
  const minis = inYear.filter((g) => tierOf(g) === 'mini');
  const quarters = [1, 2, 3, 4].map((q) => inYear.find((g) => tierOf(g) === 'quarter' && g.quarter === q) || null);
  const big = inYear.find((g) => tierOf(g) === 'year') || null;
  return { big, quarters, minis, future: live.filter((g) => tierOf(g) === 'future'), filled: (big ? 1 : 0) + quarters.filter(Boolean).length + minis.length, total: 1 + 4 + MINI_SLOTS };
};

const today = () => new Date();
const d = (s) => (s ? parseISO(String(s).slice(0, 10)) : null);

export const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
export const horizonFor = (dateStr) => { const m = differenceInMonths(d(dateStr), today()); return m < 3 ? 'h3' : m < 6 ? 'h6' : m < 12 ? 'h12' : 'h24'; };
export const monthsOut = (dateStr) => Math.max(0, differenceInMonths(d(dateStr), today()));
export const daysSince = (dateStr) => (dateStr ? differenceInDays(today(), d(dateStr)) : 0);
export const daysUntil = (dateStr) => (dateStr ? differenceInDays(d(dateStr), today()) : null);
export const dueInDays = (item) => daysUntil(item.due);
export const isStale = (item) => item.status !== 'done' && daysSince(item.updated_at) >= 30;
export const isQuickWin = (item) => item.status !== 'done' && item.impact === 3 && item.effort === 1;
export const isWin = (item) => item.status === 'done' && item.completed_at && isAfter(d(item.completed_at), startOfQuarter(today()));
export const OPEN_GOAL = ['on_track', 'at_risk', 'off_track'];
export const isOpenGoal = (g) => OPEN_GOAL.includes(g.status);
export const reviewState = (g) => {
  if (!isOpenGoal(g) || !g.review_date) return null;
  const n = daysUntil(g.review_date);
  return n < 0 ? 'overdue' : n <= 14 ? 'due' : 'ok';
};

export const goalProgress = (goal, items) => {
  const linked = items.filter((i) => i.goal_id === goal.id);
  if (goal.status === 'achieved') return { pct: 100, done: linked.filter((i) => i.status === 'done').length, total: linked.length };
  if (!linked.length) return { pct: goal.manual_progress || 0, done: 0, total: 0 };
  const done = linked.filter((i) => i.status === 'done').length;
  return { pct: Math.round((done / linked.length) * 100), done, total: linked.length };
};

export const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;
